/**
 * A text box and a sticky note, as the board stores them (SRS C4).
 *
 * Both are board objects (docs/05-DATA-MODEL.md §3.2), and both carry their words in
 * `data.content`. What separates them is the paper: a text box is words on the board
 * itself, a sticky note is words on a coloured square.
 *
 * Sizes here are CANVAS units, never screen pixels, exactly like `Stroke.canvasWidth`
 * (SRS C3). Writing therefore keeps its size against the board: zooming in on a page
 * magnifies the words along with everything drawn around them, which is what a
 * teacher zooming in to show the class a detail expects to happen.
 */

import {
  newObjectId,
  STICKY_COLORS,
  type BoardObject,
  type Layer,
  type Point,
  type StickyColor,
  type StickyData,
  type TextAlign,
  type TextData,
} from '@opal/shared';

import { readBoolean, readChoice, readNumber, readString } from './object-data.js';
import { DEFAULT_STICKY_COLOR } from './sticky-colors.js';
import { lineHeight, textHeight } from './text-layout.js';

/** Which module owns each kind — docs/04-MODULE-CONTRACTS.md §3.1.1. */
export const TEXT_MODULE = 'text';
export const STICKY_MODULE = 'stickies';

/** SRS C4 — the sizes the font control offers, in canvas units. */
export const DEFAULT_FONT_SIZE = 24;
export const MIN_FONT_SIZE = 10;
export const MAX_FONT_SIZE = 96;

/** 400 is normal, 700 is bold. SRS C4 asks for a bold switch, not a weight ladder. */
export const NORMAL_WEIGHT = 400;
export const BOLD_WEIGHT = 700;

/** How wide a new text box is before anything is typed into it, in canvas units. */
export const DEFAULT_TEXT_WIDTH = 360;

/** The gap between the words and the edge of their box. */
export const TEXT_PADDING = 6;

/** A new sticky note is a square, big enough for a sentence or two. */
export const STICKY_SIZE = 240;
export const STICKY_PADDING = 20;
export const STICKY_FONT_SIZE = 20;

const ALIGNMENTS: readonly TextAlign[] = ['left', 'center', 'right'];

/** True when this object is words in a box: a text box or a sticky note. */
export function isTextual(object: BoardObject): boolean {
  return object.kind === 'text' || object.kind === 'sticky';
}

/** What a text box carries, with a sensible answer for anything missing. */
export function textDataOf(object: BoardObject): TextData {
  return {
    content: readString(object.data, 'content', ''),
    fontSize: readNumber(object.data, 'fontSize', DEFAULT_FONT_SIZE),
    fontWeight: readNumber(object.data, 'fontWeight', NORMAL_WEIGHT),
    color: readString(object.data, 'color', '#1a1a2e'),
    align: readChoice(object.data, 'align', ALIGNMENTS, 'left'),
    hasMath: readBoolean(object.data, 'hasMath', false),
    italic: readBoolean(object.data, 'italic', false),
  };
}

/** What a sticky note carries, with the same care. */
export function stickyDataOf(object: BoardObject): StickyData {
  return {
    content: readString(object.data, 'content', ''),
    color: readChoice(object.data, 'color', STICKY_COLORS, DEFAULT_STICKY_COLOR),
    fontSize: readNumber(object.data, 'fontSize', STICKY_FONT_SIZE),
  };
}

/** The words on either kind. */
export function contentOf(object: BoardObject): string {
  return readString(object.data, 'content', '');
}

/** How big the words on either kind are, in canvas units. */
export function fontSizeOf(object: BoardObject): number {
  const fallback = object.kind === 'sticky' ? STICKY_FONT_SIZE : DEFAULT_FONT_SIZE;
  return readNumber(object.data, 'fontSize', fallback);
}

/** The gap between the words and the edge, for either kind. */
export function paddingOf(object: BoardObject): number {
  return object.kind === 'sticky' ? STICKY_PADDING : TEXT_PADDING;
}

/** How wide the words may run before they wrap, in canvas units. */
export function wrapWidthOf(object: BoardObject): number {
  return Math.max(object.width - paddingOf(object) * 2, 1);
}

export interface NewText {
  /** Where the hand went down. The box starts here. */
  readonly at: Point;
  readonly userId: string;
  readonly color: string;
  readonly fontSize: number;
  readonly bold: boolean;
  readonly italic: boolean;
  readonly align: TextAlign;
  readonly layer: Layer;
  readonly width?: number;
}

/** An empty text box, waiting to be typed into. */
export function buildText(input: NewText): BoardObject {
  const now = Date.now();
  const data: TextData = {
    content: '',
    fontSize: input.fontSize,
    fontWeight: input.bold ? BOLD_WEIGHT : NORMAL_WEIGHT,
    color: input.color,
    align: input.align,
    // Maths inside a text box is Phase 10; nothing writes this yet.
    hasMath: false,
    italic: input.italic,
  };
  const width = input.width ?? DEFAULT_TEXT_WIDTH;

  return {
    id: newObjectId(),
    kind: 'text',
    createdByModule: TEXT_MODULE,
    userId: input.userId,
    x: input.at.x,
    y: input.at.y,
    width,
    height: textHeight(1, input.fontSize) + TEXT_PADDING * 2,
    rotation: 0,
    zIndex: now,
    layer: input.layer,
    locked: false,
    data: { ...data },
    createdAt: now,
    updatedAt: now,
  };
}

export interface NewSticky {
  /** Where the hand went down. The note is centred on it. */
  readonly at: Point;
  readonly userId: string;
  readonly color: StickyColor;
  readonly fontSize?: number;
  readonly layer: Layer;
}

/** An empty sticky note, waiting to be typed into. */
export function buildSticky(input: NewSticky): BoardObject {
  const now = Date.now();
  const data: StickyData = {
    content: '',
    color: input.color,
    fontSize: input.fontSize ?? STICKY_FONT_SIZE,
  };

  return {
    id: newObjectId(),
    kind: 'sticky',
    createdByModule: STICKY_MODULE,
    userId: input.userId,
    // Placed under the hand rather than beside it: a note is a big thing, and one
    // that appeared with its corner on the click would sit where it was not asked for.
    x: input.at.x - STICKY_SIZE / 2,
    y: input.at.y - STICKY_SIZE / 2,
    width: STICKY_SIZE,
    height: STICKY_SIZE,
    rotation: 0,
    zIndex: now,
    layer: input.layer,
    locked: false,
    data: { ...data },
    createdAt: now,
    updatedAt: now,
  };
}

/** The same object with different words in it, and any other field alongside. */
export function withData(
  object: BoardObject,
  patch: Readonly<Record<string, unknown>>,
): BoardObject {
  return { ...object, data: { ...object.data, ...patch }, updatedAt: Date.now() };
}

/**
 * The same text box, grown to hold the lines it now has.
 *
 * A text box grows downwards as it is typed into and shrinks back when words are
 * deleted, so the box always matches what is in it — which matters because the box is
 * what the eraser and the select tool work on. A sticky note is a fixed square and is
 * left exactly as it is.
 */
export function fittedToLines(object: BoardObject, lines: number): BoardObject {
  if (object.kind !== 'text') return object;
  const size = fontSizeOf(object);
  const height = textHeight(lines, size) + TEXT_PADDING * 2;
  if (Math.abs(height - object.height) < 0.5) return object;
  return { ...object, height, updatedAt: Date.now() };
}

/** How many lines of this size fit inside the object, at least one. */
export function linesThatFit(object: BoardObject): number {
  const inside = object.height - paddingOf(object) * 2;
  return Math.max(Math.floor(inside / lineHeight(fontSizeOf(object))), 1);
}
